import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiUrl } from '@/config/api';
import { mapTransactionTypeToCategory } from '@/utils/transactionTypes.js';
import { useAuth } from '../context/AuthContext.jsx';
import useTariffs from '../hooks/useTariffs.js';
import useWalletTransactions from '../hooks/useWalletTransactions.js';
import useWalletBalance from '../hooks/useWalletBalance.js';
import WalletBalanceSummary from '../components/WalletBalanceSummary.jsx';

const DEFAULT_TRANSACTIONS_PAGE_SIZE = 20;
const WFP_PAYMENT_FLOW_KEY = 'wfp_payment_flow';
const PAYMENT_POLL_FAST_INTERVAL = 2500;
const PAYMENT_POLL_SYNC_INTERVAL_MS = 12_000;
const PAYMENT_POLL_TIMEOUT_MS = 90_000;

const BalancePage = () => {
  const { t, i18n } = useTranslation('dashboard');
  const { token, initializing: isLoadingAuth, isAuthenticated } = useAuth();
  const location = useLocation();
  const { topupTariffs, loading: tariffsLoading, error: tariffsError } = useTariffs(token, {
    errorMessage: t('billing.errors.tariffs'),
  });
  const [activeTab, setActiveTab] = useState('balance');
  const [selectedTariffId, setSelectedTariffId] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paymentFlowState, setPaymentFlowState] = useState('idle');
  const [paymentOrderReference, setPaymentOrderReference] = useState('');
  const [paymentStartedAt, setPaymentStartedAt] = useState(0);
  const [syncModal, setSyncModal] = useState({
    open: false,
    variant: 'success',
    amount: 0,
    balance: 0,
    currency: 'EUR',
  });
  const inFlightRef = useRef(false);
  const pollingTimerRef = useRef(null);
  const openModalTimerRef = useRef(null);
  const pollAbortControllerRef = useRef(null);
  const syncRequestedAtRef = useRef(0);
  const finalizedRef = useRef(false);
  const consumedOrderRefRef = useRef('');
  const {
    transactions,
    total: transactionsTotal,
    offset: transactionsOffset,
    limit: transactionsLimit,
    loading: transactionsLoading,
    error: transactionsError,
    loadPage: loadTransactionsPage,
    initialized: transactionsInitialized,
  } = useWalletTransactions(token, {
    pageSize: DEFAULT_TRANSACTIONS_PAGE_SIZE,
    errorMessage: t('billing.transactions.error'),
  });

  useEffect(() => {
    if (topupTariffs.length > 0) {
      setSelectedTariffId(topupTariffs[0].id);
    } else {
      setSelectedTariffId(null);
    }
  }, [topupTariffs]);

  const {
    walletBalance,
    walletLoading,
    walletError,
    reloadWallet: loadWallet,
    setWalletBalance,
  } = useWalletBalance(token, { errorMessage: t('billing.errors.wallet') });


  useEffect(() => {
    if (activeTab === 'transactions' && token && !transactionsInitialized && !transactionsLoading) {
      loadTransactionsPage(0);
    }
  }, [activeTab, loadTransactionsPage, token, transactionsInitialized, transactionsLoading]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    if (paymentFlowState !== 'waiting') {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [paymentFlowState]);

  const selectedTariff = useMemo(
    () => topupTariffs.find((tariff) => tariff.id === selectedTariffId) || null,
    [selectedTariffId, topupTariffs],
  );

  const hasTariffs = !tariffsLoading && !tariffsError && topupTariffs.length > 0;
  const isWaitingForPaymentResult = paymentFlowState === 'waiting';

  const formatEur = useMemo(() => {
    try {
      return new Intl.NumberFormat(i18n.language || 'ua', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    } catch (error) {
      return new Intl.NumberFormat('en', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    }
  }, [i18n.language]);

  const formatMoney = useCallback(
    (amount, currency = 'EUR') => {
      const value = Number(amount);
      const safeValue = Number.isFinite(value) ? value : 0;
      if (currency === 'UAH') {
        return `${formatEur.format(safeValue)} ₴`;
      }
      return `${formatEur.format(safeValue)} €`;
    },
    [formatEur],
  );

  const formatTransactionDate = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    try {
      return new Intl.DateTimeFormat(i18n.language || 'ua', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(date);
    } catch (error) {
      return date.toLocaleString();
    }
  };

  const formatTransactionAmount = (amount) => {
    const numericAmount = Number(amount) || 0;
    const sign = numericAmount > 0 ? '+' : numericAmount < 0 ? '-' : '';
    return `${sign}${formatEur.format(Math.abs(numericAmount))} €`;
  };

  const resolveAmountClassName = (amount) => {
    const numericAmount = Number(amount) || 0;
    if (numericAmount > 0) {
      return 'amount-positive';
    }
    if (numericAmount < 0) {
      return 'amount-negative';
    }
    return 'amount-neutral';
  };

  const hasPreviousPage = transactionsOffset > 0;
  const hasNextPage = transactionsOffset + transactions.length < transactionsTotal;
  const transactionsRangeFrom = transactionsTotal === 0 ? 0 : transactionsOffset + 1;
  const transactionsRangeTo = Math.min(transactionsOffset + transactions.length, transactionsTotal);
  const transactionsPageSize = transactionsLimit || DEFAULT_TRANSACTIONS_PAGE_SIZE;

  const orderReferenceFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return params.get('orderReference') || '';
  }, [location.search]);

  const clearOrderReferenceFromUrl = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const params = new URLSearchParams(window.location.search || '');
    params.delete('orderReference');
    const nextSearch = params.toString();
    const nextUrl = `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`;
    window.history.replaceState(null, '', nextUrl);
  }, [location.pathname]);

  const startWaitingForPayment = useCallback((orderReference, startedAt = Date.now(), persistToSession = false) => {
    const normalizedOrderReference = String(orderReference || '').trim();
    if (!normalizedOrderReference) {
      return;
    }

    if (consumedOrderRefRef.current === normalizedOrderReference) {
      return;
    }

    if (persistToSession && typeof window !== 'undefined') {
      sessionStorage.setItem(
        WFP_PAYMENT_FLOW_KEY,
        JSON.stringify({
          orderReference: normalizedOrderReference,
          startedAt,
        }),
      );
    }

    finalizedRef.current = false;
    syncRequestedAtRef.current = 0;
    setSyncModal((prev) => (prev.open ? { ...prev, open: false } : prev));
    setPaymentOrderReference(normalizedOrderReference);
    setPaymentStartedAt(startedAt);
    setPaymentFlowState('waiting');
  }, []);

  const finalizePaymentFlow = useCallback(
    ({ variant, amount = 0, balance = walletBalance, currency = 'EUR', orderReference = '' }) => {
      if (finalizedRef.current) {
        return;
      }

      const normalizedOrderReference = String(orderReference || paymentOrderReference || '').trim();
      finalizedRef.current = true;
      if (pollingTimerRef.current) {
        window.clearTimeout(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
      if (pollAbortControllerRef.current) {
        pollAbortControllerRef.current.abort();
        pollAbortControllerRef.current = null;
      }
      inFlightRef.current = false;
      syncRequestedAtRef.current = 0;
      if (normalizedOrderReference) {
        consumedOrderRefRef.current = normalizedOrderReference;
      }
      setPaymentFlowState('done');
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem(WFP_PAYMENT_FLOW_KEY);
      }
      clearOrderReferenceFromUrl();

      if (openModalTimerRef.current) {
        window.clearTimeout(openModalTimerRef.current);
      }
      openModalTimerRef.current = window.setTimeout(() => {
        setSyncModal({
          open: true,
          variant,
          amount,
          balance,
          currency,
        });
      }, 0);

      setPaymentOrderReference('');
      setPaymentStartedAt(0);
    },
    [clearOrderReferenceFromUrl, paymentOrderReference, walletBalance],
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (paymentFlowState === 'waiting') {
      return;
    }

    if (orderReferenceFromQuery && consumedOrderRefRef.current !== orderReferenceFromQuery) {
      startWaitingForPayment(orderReferenceFromQuery, Date.now(), true);
      return;
    }

    const rawValue = sessionStorage.getItem(WFP_PAYMENT_FLOW_KEY);
    if (!rawValue) {
      return;
    }

    try {
      const parsed = JSON.parse(rawValue);
      if (parsed?.orderReference && consumedOrderRefRef.current !== parsed.orderReference) {
        startWaitingForPayment(parsed.orderReference, Number(parsed.startedAt) || Date.now(), false);
      }
    } catch (error) {
      sessionStorage.removeItem(WFP_PAYMENT_FLOW_KEY);
    }
  }, [orderReferenceFromQuery, paymentFlowState, startWaitingForPayment]);


  useEffect(() => {
    return () => {
      if (openModalTimerRef.current) {
        window.clearTimeout(openModalTimerRef.current);
        openModalTimerRef.current = null;
      }
    };
  }, []);
  useEffect(() => {
    if (isLoadingAuth || !isAuthenticated || !token || !isWaitingForPaymentResult || !paymentOrderReference) {
      return undefined;
    }

    let cancelled = false;

    const clearTimers = () => {
      if (pollingTimerRef.current) {
        window.clearTimeout(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
      if (pollAbortControllerRef.current) {
        pollAbortControllerRef.current.abort();
        pollAbortControllerRef.current = null;
      }
      inFlightRef.current = false;
    };

    const resolveFinalStatus = (status) => {
      const normalized = String(status || '').trim().toLowerCase();
      if (normalized === 'approved') {
        return 'success';
      }
      if (normalized && normalized !== 'pending') {
        return 'fail';
      }
      return 'pending';
    };

    const finalizeWithPayload = async (variant, payload) => {
      if (finalizedRef.current || paymentFlowState !== 'waiting') {
        return;
      }

      const amountValue = Number(payload?.amount) || 0;
      const balanceValue = Number(payload?.balance);
      const currency = typeof payload?.currency === 'string' ? payload.currency : 'EUR';
      if (Number.isFinite(balanceValue)) {
        setWalletBalance(balanceValue);
      }
      finalizePaymentFlow({
        variant,
        amount: amountValue,
        balance: Number.isFinite(balanceValue) ? balanceValue : walletBalance,
        currency,
        orderReference: paymentOrderReference,
      });
      if (variant === 'success') {
        await loadWallet();
      }
    };

    const scheduleNextPoll = () => {
      if (cancelled || finalizedRef.current || paymentFlowState !== 'waiting') {
        return;
      }

      pollingTimerRef.current = window.setTimeout(runPollingCycle, PAYMENT_POLL_FAST_INTERVAL);
    };

    const runPollingCycle = async () => {
      if (
        cancelled
        || finalizedRef.current
        || inFlightRef.current
        || paymentFlowState !== 'waiting'
        || syncModal.open
      ) {
        return;
      }

      const elapsed = Date.now() - paymentStartedAt;
      if (elapsed >= PAYMENT_POLL_TIMEOUT_MS) {
        finalizePaymentFlow({ variant: 'fail', orderReference: paymentOrderReference });
        return;
      }

      inFlightRef.current = true;
      const controller = new AbortController();
      pollAbortControllerRef.current = controller;

      try {
        const statusResponse = await fetch(apiUrl(`/api/payments/topups/${encodeURIComponent(paymentOrderReference)}`), {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        });
        const statusData = await statusResponse.json().catch(() => ({}));
        if (!statusResponse.ok) {
          throw new Error(statusData.error || 'Failed to get payment status');
        }

        const statusFinal = resolveFinalStatus(statusData?.status);
        if (statusFinal !== 'pending') {
          await finalizeWithPayload(statusFinal, statusData);
          return;
        }

        const now = Date.now();
        if (now - syncRequestedAtRef.current >= PAYMENT_POLL_SYNC_INTERVAL_MS) {
          syncRequestedAtRef.current = now;
          const syncResponse = await fetch(
            apiUrl(`/api/payments/wayforpay/topups/${encodeURIComponent(paymentOrderReference)}/sync`),
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
              },
              signal: controller.signal,
            },
          );
          const syncData = await syncResponse.json().catch(() => ({}));
          if (!syncResponse.ok) {
            throw new Error(syncData.error || 'Failed to sync payment status');
          }

          const syncFinal = resolveFinalStatus(syncData?.status);
          if (syncFinal !== 'pending') {
            await finalizeWithPayload(syncFinal, syncData);
            return;
          }
        }

        scheduleNextPoll();
      } catch (error) {
        if (!cancelled && error.name !== 'AbortError') {
          scheduleNextPoll();
        }
      } finally {
        inFlightRef.current = false;
        pollAbortControllerRef.current = null;
      }
    };

    runPollingCycle();

    return () => {
      cancelled = true;
      clearTimers();
    };
  }, [
    isAuthenticated,
    isLoadingAuth,
    isWaitingForPaymentResult,
    loadWallet,
    finalizePaymentFlow,
    paymentOrderReference,
    paymentStartedAt,
    paymentFlowState,
    syncModal.open,
    token,
    walletBalance,
  ]);

  const handleCloseSyncModal = () => {
    setSyncModal((prev) => ({ ...prev, open: false }));
  };

  const handlePrevPage = () => {
    if (!hasPreviousPage || transactionsLoading || isWaitingForPaymentResult) {
      return;
    }

    const nextOffset = Math.max(transactionsOffset - transactionsPageSize, 0);
    loadTransactionsPage(nextOffset);
  };

  const handleNextPage = () => {
    if (!hasNextPage || transactionsLoading || isWaitingForPaymentResult) {
      return;
    }

    loadTransactionsPage(transactionsOffset + transactionsPageSize);
  };

  const handlePay = async () => {
    if (!selectedTariff || paymentLoading || isWaitingForPaymentResult) {
      return;
    }
    setPaymentLoading(true);
    setPaymentError('');
    try {
      const response = await fetch(apiUrl('/api/payments/wayforpay/purchase'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          topupTariffId: selectedTariff.id,
          language: i18n.language,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || t('billing.payment.error'));
      }
      const data = await response.json();
      const form = document.createElement('form');
      form.method = 'POST';
      form.acceptCharset = 'utf-8';
      form.action = data.actionUrl;

      Object.entries(data.fields || {}).forEach(([name, value]) => {
        const items = Array.isArray(value) ? value : [value];
        items.forEach((item) => {
          if (item === undefined || item === null) {
            return;
          }
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = String(item);
          form.appendChild(input);
        });
      });

      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      setPaymentError(error.message || t('billing.payment.error'));
    } finally {
      setPaymentLoading(false);
    }
  };

  return (
    <div className="page-card balance-page">
      {isWaitingForPaymentResult && (
        <div className="payment-check-overlay" role="status" aria-live="polite" aria-busy="true">
          <div className="payment-check-overlay__content">
            <div className="spinner payment-check-overlay__spinner" />
            <p className="payment-check-overlay__text">{t('billing.paymentChecking')}</p>
          </div>
        </div>
      )}

      <div className="page-card-header balance-page__header">
        <div>
          <h1>{t('billing.heading.title')}</h1>
        </div>
        <WalletBalanceSummary
          balance={walletBalance}
          loading={walletLoading}
          error={walletError}
        />
      </div>

      <div className="balance-tabs type-tabs">
        <button
          type="button"
          className={activeTab === 'balance' ? 'type-tab active' : 'type-tab'}
          onClick={() => setActiveTab('balance')}
          disabled={isWaitingForPaymentResult}
        >
          {t('billing.tabs.balance')}
        </button>
        <button
          type="button"
          className={activeTab === 'transactions' ? 'type-tab active' : 'type-tab'}
          onClick={() => setActiveTab('transactions')}
          disabled={isWaitingForPaymentResult}
        >
          {t('billing.tabs.transactions')}
        </button>
      </div>

      {activeTab === 'balance' && (
        <>
          <div className="tariff-section">
            <div className="tariff-section__header">
              <div>
                <h2>{t('billing.tariffs.title')}</h2>
              </div>
            </div>

            {tariffsLoading && <p className="form-hint">{t('billing.tariffs.loading')}</p>}
            {tariffsError && <p className="form-error">{tariffsError}</p>}
            {!tariffsLoading && !tariffsError && topupTariffs.length === 0 && (
              <div className="tariff-empty">{t('billing.tariffs.empty')}</div>
            )}

            {!tariffsLoading && !tariffsError && topupTariffs.length > 0 && (
              <div className="tariff-card-list">
                {topupTariffs.map((tariff) => (
                  <button
                    key={tariff.id}
                    type="button"
                    className={`tariff-card ${selectedTariffId === tariff.id ? 'tariff-card--active' : ''}`}
                    onClick={() => setSelectedTariffId(tariff.id)}
                    disabled={isWaitingForPaymentResult}
                  >
                    <div className="tariff-card__price">
                      {t('billing.tariffs.amount', { value: formatEur.format(tariff.amountEur) })}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {hasTariffs && selectedTariff && (
            <div className="payment-panel">
              <div>
                <p className="payment-panel__title">
                  {t('billing.payment.title', { amount: formatEur.format(selectedTariff.amountEur) })}
                </p>
                <p className="payment-panel__hint">
                  {t('billing.payment.hint', { amount: formatEur.format(selectedTariff.amountEur) })}
                </p>
              </div>
              <div className="payment-panel__actions">
                <button
                  type="button"
                  className="primary-button"
                  onClick={handlePay}
                  disabled={paymentLoading || isWaitingForPaymentResult}
                >
                  {paymentLoading ? t('billing.payment.loading') : t('billing.payment.button')}
                </button>
                {paymentError && <p className="form-error">{paymentError}</p>}
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'transactions' && (
        <div className="transactions-section">
          <div className="transactions-header">
            <h2>{t('billing.tabs.transactions')}</h2>
            <p className="page-description">{t('billing.transactions.description')}</p>
          </div>

          {transactionsLoading && (
            <div className="transactions-state transactions-state--loading">
              {t('billing.transactions.loading')}
            </div>
          )}

          {!transactionsLoading && transactionsError && (
            <div className="transactions-state transactions-state--error">
              <p className="form-error">{transactionsError}</p>
              <button
                type="button"
                className="secondary-button"
                onClick={() => loadTransactionsPage(transactionsOffset || 0)}
                disabled={isWaitingForPaymentResult}
              >
                {t('billing.transactions.retry')}
              </button>
            </div>
          )}

          {!transactionsLoading && !transactionsError && transactionsInitialized && transactions.length === 0 && (
            <div className="transactions-state">{t('billing.transactions.empty')}</div>
          )}

          {!transactionsLoading && !transactionsError && transactions.length > 0 && (
            <>
              <div className="games-table-wrapper games-table-wrapper--responsive transactions-table-wrapper">
                <table className="games-table transactions-table">
                  <thead>
                    <tr>
                      <th>{t('billing.transactions.columns.date')}</th>
                      <th>{t('billing.transactions.columns.type')}</th>
                      <th>{t('billing.transactions.columns.amount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx) => (
                      <tr key={tx.id}>
                        <td>{formatTransactionDate(tx.createdAt)}</td>
                        <td>{t(`billing.transactions.types.${mapTransactionTypeToCategory(tx.type)}`)}</td>
                        <td>
                          <span className={resolveAmountClassName(tx.amountEur)}>
                            {formatTransactionAmount(tx.amountEur)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="transactions-cards">
                {transactions.map((tx) => (
                  <div key={`card-${tx.id}`} className="transaction-card">
                    <div className="transaction-card__row">
                      <span className="transaction-card__label">{t('billing.transactions.columns.date')}</span>
                      <span>{formatTransactionDate(tx.createdAt)}</span>
                    </div>
                    <div className="transaction-card__row">
                      <span className="transaction-card__label">{t('billing.transactions.columns.type')}</span>
                      <span>{t(`billing.transactions.types.${mapTransactionTypeToCategory(tx.type)}`)}</span>
                    </div>
                    <div className="transaction-card__row">
                      <span className="transaction-card__label">{t('billing.transactions.columns.amount')}</span>
                      <span className={resolveAmountClassName(tx.amountEur)}>
                        {formatTransactionAmount(tx.amountEur)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="transactions-pagination">
                <button
                  type="button"
                  className="secondary-button"
                  disabled={!hasPreviousPage || transactionsLoading || isWaitingForPaymentResult}
                  onClick={handlePrevPage}
                >
                  {t('billing.transactions.pagination.prev')}
                </button>
                <span className="transactions-pagination__info">
                  {t('billing.transactions.pagination.info', {
                    from: transactionsRangeFrom,
                    to: transactionsRangeTo,
                    total: transactionsTotal,
                  })}
                </span>
                <button
                  type="button"
                  className="secondary-button"
                  disabled={!hasNextPage || transactionsLoading || isWaitingForPaymentResult}
                  onClick={handleNextPage}
                >
                  {t('billing.transactions.pagination.next')}
                </button>
              </div>
            </>
          )}
        </div>
      )}
      {syncModal.open && !isWaitingForPaymentResult && (
        <div className="modal-backdrop" role="alertdialog" aria-modal="true">
          <div className="modal confirm-modal">
            <p className="modal-description">
              {(syncModal.variant === 'success'
                ? t('billing.topup.successText', {
                    amount: formatMoney(syncModal.amount, syncModal.currency),
                    balance: formatMoney(syncModal.balance, syncModal.currency),
                  })
                : t('billing.topup.failText')
              )
                .split('\n')
                .map((line, index) => (
                  <span key={line || index} className="modal-description-line">
                    {line}
                  </span>
                ))}
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className={syncModal.variant === 'success' ? 'primary-button' : 'secondary-button'}
                onClick={handleCloseSyncModal}
              >
                {syncModal.variant === 'success' ? t('billing.topup.successBtn') : t('billing.topup.failBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BalancePage;
